import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { modalKeyValue, modalPrimaryButton } from "./ModalShell";
import { createShare, emailShare, formatCode, myShares, revokeShare, updateShare } from "./shareApi";
import { useShareFor, useShareSnapshot } from "./shareSnapshotStore";

/**
 * 결과 공유 버튼 + 작은 창(2026-10-07) — 결과 탭 바 오른쪽 위.
 * Figma·Miro·Claude 처럼 버튼 바로 아래에 붙는 작은 창, 가장 큰 동작은 [링크 복사](한 번에 만들고 복사).
 * 내용은 누른 시점의 사본(Claude 식) — 같은 검색어·세번으로 이미 공유했으면 새로 만들지 않고 [링크 갱신]으로 같은 주소의 내용만 바꾼다.
 * 받는 사람은 로그인 없이 /s/토큰 으로 본다(SharedResultPage).
 */
const WIDTH = 380;
const norm = (s) => String(s || "").trim().replace(/\s+/g, " ");
const digits = (s) => String(s || "").replace(/\D/g, "");
const btn = { border: "1px solid var(--audit-border)", background: "transparent", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer", color: "var(--audit-text)", whiteSpace: "nowrap", flexShrink: 0, font: "inherit" };
const input = { width: "100%", boxSizing: "border-box", border: "1px solid var(--audit-border)", borderRadius: 8, padding: "7px 10px", font: "inherit", fontSize: 13, background: "var(--audit-bg-elevated, #fff)", color: "var(--audit-text)", minWidth: 0 };
const muted = { fontSize: 12, color: "var(--audit-text-muted)" };
const linkBtn = { border: 0, background: "transparent", padding: 0, font: "inherit", fontSize: 12, color: "var(--audit-text-muted)", cursor: "pointer" };

/** 같은 검색어·세번의 살아 있는 내 공유 */
export function findActiveShare(list, snapshot) {
  const q = norm(snapshot?.query), c = digits(snapshot?.code);
  return (list || []).find((s) => !s.revoked && !s.expired && norm(s.query) === q && digits(s.code) === c) || null;
}

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

export default function ShareButton({ q, style, fetchImpl = fetch }) {
  const snap = useShareSnapshot(q);
  const snapshot = snap || { query: norm(q) };
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  return (
    <>
      <button ref={ref} type="button" data-testid="results-share" aria-haspopup="dialog" aria-expanded={open}
        onClick={() => setOpen((v) => !v)} style={style} title="이 결과를 링크·메일로 공유">공유</button>
      {/* 색 변수(--audit-*)는 .audit-root 에 있다 — body 로 띄우면 주 버튼 바탕이 사라진다(2026-10-07 운영 확인) */}
      {open && createPortal(<ShareCard anchor={ref} snapshot={snapshot} hasResult={Boolean(snap)} onClose={() => setOpen(false)} fetchImpl={fetchImpl} />,
        ref.current?.closest?.(".audit-root") || document.body)}
    </>
  );
}

/**
 * 상단 머리 [공유](2026-10-10 사용자: 공유는 맨 위에 — 메모도 공유). kind = "search"(검색 결과) | "memo"(고른 메모) | null.
 * 공유할 것이 없으면 눌러도 안내만 한다.
 */
export function HeaderShareButton({ kind, compact = false, fetchImpl = fetch }) {
  const stored = useShareFor(kind);
  // 검색 화면인데 지도 결과가 아직 없으면 검색어만이라도 공유한다(종전 탭 줄 버튼과 같게)
  const urlQ = kind === "search" && typeof window !== "undefined" ? norm(new URLSearchParams(window.location.search).get("sq")) : "";
  const snap = stored || (urlQ ? { query: urlQ } : null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const none = !snap;
  const title = none
    ? (kind === "memo" ? "공유할 메모를 고르세요" : "검색 결과나 메모를 열면 공유할 수 있습니다")
    : ({ memo: "이 메모를", precedent: "이 품목분류사례를", hsk: "이 세번을" }[kind] || "이 결과를") + " 링크·메일로 공유";
  return (
    <>
      <button ref={ref} type="button" data-testid="header-share" aria-haspopup="dialog" aria-expanded={open} disabled={none}
        onClick={() => setOpen((v) => !v)} title={title}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, border: 0, borderRadius: 8, padding: compact ? "5px 10px" : "6px 14px",
          fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", cursor: none ? "default" : "pointer",
          background: none ? "#e5e7eb" : "var(--audit-accent, #1a73e8)", color: none ? "#6b7280" : "#fff" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6" /><path d="M22 11h-6" />
        </svg>
        공유
      </button>
      {open && snap && createPortal(
        <ShareCard anchor={ref} snapshot={snap} hasResult={Boolean(stored)} onClose={() => setOpen(false)} fetchImpl={fetchImpl} />,
        ref.current?.closest?.(".audit-root") || document.body)}
    </>
  );
}

export function ShareCard({ anchor, snapshot, hasResult = true, onClose, fetchImpl = fetch }) {
  const box = useRef(null);
  const [pos, setPos] = useState({ top: 60, right: 16 });
  const [days, setDays] = useState(30);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [active, setActive] = useState(null);     // {token, url, expires_at, views?}
  const [reused, setReused] = useState(false);    // 예전에 만든 링크를 다시 쓰는 중
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [to, setTo] = useState("");
  const [mailMsg, setMailMsg] = useState("");
  const [list, setList] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const place = useCallback(() => {
    const r = anchor?.current?.getBoundingClientRect?.();
    if (!r) return;
    setPos({ top: Math.round(r.bottom + 6), right: Math.max(12, Math.round(window.innerWidth - r.right)) });
  }, [anchor]);
  useLayoutEffect(() => { place(); }, [place]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onDown = (e) => {
      if (box.current?.contains(e.target) || anchor?.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onDown); window.removeEventListener("resize", place); };
  }, [anchor, onClose, place]);

  const loadList = useCallback(() => myShares(fetchImpl).then((j) => { setList(j.items || []); return j.items || []; }).catch(() => { setList([]); return []; }), [fetchImpl]);
  useEffect(() => {
    loadList().then((items) => {
      const hit = findActiveShare(items, snapshot);
      if (hit) { setActive(hit); setReused(true); }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ensure = async () => {
    if (active) return active;
    const j = await createShare({ ...snapshot, note, days }, fetchImpl);
    setActive(j); setReused(false);
    loadList();
    return j;
  };

  const copyLink = async () => {
    setBusy(true); setMsg(""); setCopied(false);
    try {
      const s = await ensure();
      if (await copyText(s.url)) setCopied(true);
      else setMsg("자동 복사가 막혔습니다. 아래 주소를 선택해 복사해 주세요.");
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true); setMsg(""); setCopied(false);
    try {
      const j = await updateShare(active.token, { ...snapshot, note, days }, fetchImpl);
      setActive((a) => ({ ...a, ...j })); setReused(false);
      setMsg("같은 주소의 내용을 지금 화면으로 바꿨습니다.");
      loadList();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const sendMail = async () => {
    setBusy(true); setMailMsg("");
    try {
      const s = await ensure();
      const j = await emailShare(s.token, to, note, fetchImpl);
      setMailMsg(j.failed?.length ? `보냄 ${j.sent.length}명 · 못 보냄: ${j.failed.join(", ")}` : `${j.sent.length}명에게 보냈습니다.`);
      if (!j.failed?.length) setTo("");
      loadList();
    } catch (e) { setMailMsg(e.message); } finally { setBusy(false); }
  };

  const nativeShare = async () => {
    try {
      const s = await ensure();
      await navigator.share({ title: "LoginHS 세번 분류 결과", text: `${snapshot.query || ""} — ${formatCode(snapshot.code)}`.trim(), url: s.url });
    } catch { /* 사용자가 닫음 */ }
  };

  const revoke = async (t) => {
    try { await revokeShare(t, fetchImpl); if (active?.token === t) { setActive(null); setReused(false); setCopied(false); } loadList(); }
    catch (e) { setMsg(e.message); }
  };

  const nSources = snapshot.sources?.length || 0;
  const liveCount = (list || []).filter((s) => !s.revoked && !s.expired).length;
  return (
    <div ref={box} role="dialog" aria-label="결과 공유" data-testid="share-card"
      style={{ position: "fixed", top: pos.top, right: pos.right, width: WIDTH, maxWidth: "calc(100vw - 24px)", zIndex: 1300, background: "var(--audit-bg-elevated, #fff)", color: "var(--audit-text)", border: "1px solid var(--audit-border)", borderRadius: 12, boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)", padding: 14, display: "grid", gap: 12, fontSize: 13 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <b style={{ fontSize: 14 }}>결과 공유</b>
        <button type="button" onClick={onClose} aria-label="닫기" style={{ ...linkBtn, fontSize: 16 }}>✕</button>
      </div>

      <div data-testid="share-summary">
        {snapshot.code && <span style={{ ...modalKeyValue, fontFamily: "Consolas, monospace" }}>{formatCode(snapshot.code)}</span>}
        {snapshot.query && <span style={{ marginLeft: snapshot.code ? 8 : 0 }}>{snapshot.query}</span>}
        <div style={{ ...muted, marginTop: 3 }}>
          {snapshot.type === "memo"
            ? `메모 · 본문 ${String(snapshot.body || "").length}자${snapshot.code ? " · 세번·세율 요약" : ""}${snapshot.requirement ? " · 요건" : ""} · 지금 저장된 내용 사본`
            : snapshot.type === "precedent"
              ? "품목분류사례 · 결정세번·물품설명·결정사유·관세청 원문 링크 · 내용은 원장에서 채웁니다"
              : !snapshot.query && snapshot.code ? "세번 · 품명·세율 요약"
              : hasResult ? `출처 ${nSources}줄 · 세율 요약 · 지금 화면 기준 사본` : "세번 결과가 아직 없어 검색어만 공유됩니다"}
        </div>
      </div>

      {active && reused && (
        <div data-testid="share-existing" style={{ ...muted, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span>이미 공유한 링크가 있습니다 · {String(active.expires_at).slice(0, 10)}까지{active.views != null ? ` · 열람 ${active.views}` : ""}</span>
          <button type="button" style={{ ...btn, padding: "3px 10px", fontSize: 12 }} onClick={refresh} disabled={busy} data-testid="share-refresh"
            title="같은 주소를 두고 내용만 지금 화면으로 바꿉니다">링크 갱신</button>
        </div>
      )}

      <div style={{ display: "grid", gap: 6 }}>
        <button type="button" data-testid="share-copy" onClick={copyLink} disabled={busy}
          style={{ ...modalPrimaryButton, background: "var(--audit-accent, #1a73e8)", width: "100%", whiteSpace: "nowrap", padding: "9px 12px" }}>
          {busy && !active ? "링크 만드는 중…" : copied ? "복사했습니다 ✓" : "링크 복사"}
        </button>
        {active && (
          <input style={{ ...input, fontFamily: "Consolas, monospace", fontSize: 12 }} readOnly value={active.url}
            onFocus={(e) => e.target.select()} aria-label="공유 링크" data-testid="share-url" />
        )}
        <div style={{ ...muted, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span>링크가 있는 누구나 보기 · 로그인 불필요 ·</span>
          {active ? <span>{String(active.expires_at).slice(0, 10)}까지</span> : (
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="링크 열리는 기간"
              style={{ font: "inherit", fontSize: 12, border: "1px solid var(--audit-border)", borderRadius: 6, padding: "1px 4px", background: "transparent", color: "var(--audit-text)" }}>
              {[7, 30, 90].map((d) => <option key={d} value={d}>{d}일</option>)}
            </select>
          )}
        </div>
      </div>

      {noteOpen ? (
        <input style={input} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} autoFocus
          placeholder="받는 사람에게 남길 말 · 예: 다음 주 수입분 이 세번으로 진행 예정" aria-label="남길 말" />
      ) : (
        <button type="button" style={{ ...linkBtn, justifySelf: "start" }} onClick={() => setNoteOpen(true)} data-testid="share-note-open">+ 남길 말 적기</button>
      )}
      {noteOpen && active && reused && <div style={muted}>남길 말을 바꿨다면 [링크 갱신]을 눌러야 받는 쪽에 반영됩니다.</div>}

      <div>
        <div style={{ ...muted, marginBottom: 4 }}>메일로 보내기 · 한 번에 5명</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input style={input} value={to} onChange={(e) => setTo(e.target.value)} placeholder="받는 사람 메일 (쉼표로 여러 명)" aria-label="받는 사람 메일" data-testid="share-mail-to" />
          <button type="button" style={btn} onClick={sendMail} disabled={busy || !to.trim()} data-testid="share-mail-send">보내기</button>
        </div>
        {mailMsg && <div style={{ fontSize: 12, marginTop: 4 }} role="status">{mailMsg}</div>}
      </div>

      {canNativeShare && <button type="button" style={{ ...btn, justifySelf: "start" }} onClick={nativeShare}>휴대폰으로 공유(카톡·문자)</button>}
      {msg && <div style={{ fontSize: 12 }} role="alert">{msg}</div>}

      <div style={{ borderTop: "1px solid var(--audit-border)", paddingTop: 8 }}>
        <button type="button" style={linkBtn} onClick={() => setListOpen((v) => !v)} data-testid="share-list-toggle" aria-expanded={listOpen}>
          내가 공유한 링크{list ? ` ${liveCount}개` : ""} {listOpen ? "▴" : "▾"}
        </button>
        {listOpen && list && (
          <div style={{ display: "grid", gap: 2, maxHeight: 180, overflow: "auto", marginTop: 6 }} data-testid="share-list">
            {list.length === 0 && <div style={muted}>아직 없습니다.</div>}
            {list.map((s) => {
              const dead = s.revoked || s.expired;
              return (
                <div key={s.token} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, fontSize: 12, padding: "4px 0", borderBottom: "1px solid var(--audit-border)", opacity: dead ? 0.55 : 1 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <b style={{ fontFamily: "Consolas, monospace" }}>{formatCode(s.code)}</b> {s.query} · 열람 {s.views}{s.emails ? ` · 메일 ${s.emails}` : ""} · {dead ? (s.revoked ? "끊음" : "만료") : `${String(s.expires_at).slice(0, 10)}까지`}
                  </span>
                  {!dead && <button type="button" style={{ ...btn, padding: "1px 8px", fontSize: 12 }} onClick={() => revoke(s.token)}>끊기</button>}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ ...muted, fontSize: 11 }}>LoginHS 의 제안이며 확정 분류가 아닙니다 · 관세사 이력은 비율만, 메모·첨부는 가지 않습니다.</div>
    </div>
  );
}
